import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface GenerateIconsOptions {
  sourceImageUrl: string;
  sizes: number[];
  onProgress?: (current: number, total: number) => void;
}

interface GenerationResult {
  size: number;
  url: string;
  success: boolean;
  error?: string;
}

interface GenerateIconsResult {
  results: GenerationResult[];
  successCount: number;
  totalCount: number;
  allSuccessful: boolean;
}

export const usePWAIconGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const resizeImageToCanvas = async (
    sourceUrl: string,
    targetWidth: number,
    targetHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // Set canvas dimensions
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Draw image scaled to fit canvas
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], `pwa-icon-${targetWidth}x${targetHeight}.png`, {
                type: 'image/png',
                lastModified: Date.now(),
              });
              resolve(file);
            } else {
              reject(new Error('Failed to create resized image'));
            }
          },
          'image/png',
          1.0
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load source image'));
      };

      // Handle both external URLs and data URLs
      img.src = sourceUrl;
    });
  };

  const uploadGeneratedIcon = async (iconFile: File, size: number): Promise<{ url: string; success: boolean; error?: string }> => {
    try {
      // Generate unique filename
      const fileName = `pwa-icon-${size}x${size}-${Date.now()}.png`;
      const filePath = `pwa-icons/${fileName}`;

      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(filePath, iconFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      return {
        url: urlData.publicUrl,
        success: true
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      return {
        url: '',
        success: false,
        error: errorMessage
      };
    }
  };

  const generateMissingIcons = async ({
    sourceImageUrl,
    sizes,
    onProgress
  }: GenerateIconsOptions): Promise<GenerateIconsResult> => {
    setIsGenerating(true);
    const results: GenerationResult[] = [];
    let successCount = 0;

    try {
      for (let i = 0; i < sizes.length; i++) {
        const size = sizes[i];
        onProgress?.(i + 1, sizes.length);

        try {
          // Resize the image
          const resizedFile = await resizeImageToCanvas(sourceImageUrl, size, size);
          
          // Upload the resized image
          const uploadResult = await uploadGeneratedIcon(resizedFile, size);
          
          if (uploadResult.success) {
            successCount++;
            results.push({
              size,
              url: uploadResult.url,
              success: true
            });
          } else {
            results.push({
              size,
              url: '',
              success: false,
              error: uploadResult.error
            });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          results.push({
            size,
            url: '',
            success: false,
            error: errorMessage
          });
        }
      }

      const allSuccessful = successCount === sizes.length;
      
      if (allSuccessful) {
        toast({
          title: "Icons generated successfully",
          description: `All ${successCount} icon sizes have been generated and uploaded.`,
        });
      } else if (successCount > 0) {
        toast({
          title: "Partial success",
          description: `${successCount} of ${sizes.length} icons generated successfully.`,
          variant: "default",
        });
      } else {
        toast({
          title: "Generation failed",
          description: "Failed to generate any icons. Please check your source image.",
          variant: "destructive",
        });
      }

      return {
        results,
        successCount,
        totalCount: sizes.length,
        allSuccessful
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Generation failed';
      
      toast({
        title: "Generation failed",
        description: errorMessage,
        variant: "destructive",
      });

      return {
        results,
        successCount,
        totalCount: sizes.length,
        allSuccessful: false
      };
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateMissingIcons,
    isGenerating
  };
};